import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Plus, Trash2, Edit2, Eye, EyeOff, Briefcase, 
  MapPin, Clock, Link2, Copy, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobListingsPage = () => {
  const { isOwner } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    position_type: 'Full-time',
    service_area: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.id}`, formData);
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/jobs`, formData);
        toast.success('Job created successfully');
      }
      closeDialog();
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job listing?')) return;
    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast.success('Job deleted');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const toggleJobStatus = async (job) => {
    const newStatus = job.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/jobs/${job.id}`, { status: newStatus });
      toast.success(`Job ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      position_type: job.position_type,
      service_area: job.service_area,
      description: job.description,
      status: job.status
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingJob(null);
    setFormData({
      title: '',
      position_type: 'Full-time',
      service_area: '',
      description: '',
      status: 'active'
    });
  };

  const copyJobLink = (jobId) => {
    const link = `${window.location.origin}/apply/${jobId}`;
    navigator.clipboard.writeText(link);
    toast.success('Job link copied to clipboard!');
  };

  const activeJobs = jobs.filter(j => j.status === 'active').length;
  const totalApplications = jobs.reduce((sum, j) => sum + (j.applications || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="job-listings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">Job Listings</h1>
          <p className="text-[#666666] mt-1">Manage your open positions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchJobs} variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="create-job-btn">
                <Plus className="mr-2 h-4 w-4" />
                Create Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingJob ? 'Edit Job Listing' : 'Create New Job'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Companion Caregiver"
                    required
                    data-testid="job-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position_type">Position Type</Label>
                  <Select 
                    value={formData.position_type} 
                    onValueChange={(value) => setFormData({ ...formData, position_type: value })}
                  >
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Either">Either</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_area">Service Area / Location</Label>
                  <Input
                    id="service_area"
                    value={formData.service_area}
                    onChange={(e) => setFormData({ ...formData, service_area: e.target.value })}
                    placeholder="e.g., Central New Jersey"
                    required
                    data-testid="job-area-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Job Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the role and responsibilities..."
                    className="min-h-32"
                    required
                    data-testid="job-description-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={closeDialog} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#E8722A] hover:bg-[#D6621F]" data-testid="save-job-btn">
                    {editingJob ? 'Update Job' : 'Create Job'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Shareable Links */}
      <Card className="border-l-4 border-[#E8722A]" data-testid="shareable-links-card">
        <CardContent className="pt-6 pb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-[#E8722A]/10 rounded-lg">
                <Link2 className="h-5 w-5 text-[#E8722A]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#4B2D6E] text-sm">Shareable Careers Link</h3>
                <p className="text-xs text-[#999]">Share on Indeed, LinkedIn, social media — no login required</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-gray-100 px-3 py-1.5 rounded-md text-[#333] font-mono max-w-xs truncate" data-testid="careers-link-display">
                {window.location.origin}/careers
              </code>
              <Button
                variant="outline"
                size="sm"
                className="border-[#E8722A] text-[#E8722A] hover:bg-[#E8722A]/5"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/careers`);
                  toast.success('Careers page link copied!');
                }}
                data-testid="copy-careers-link-btn"
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
              </Button>
              {jobs.filter(j => j.status === 'active').length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#4B2D6E] text-[#4B2D6E] hover:bg-[#4B2D6E]/5"
                  onClick={() => {
                    const firstActive = jobs.find(j => j.status === 'active');
                    if (firstActive) {
                      navigator.clipboard.writeText(`${window.location.origin}/apply/${firstActive.id}`);
                      toast.success(`Direct application link copied for "${firstActive.title}"!`);
                    }
                  }}
                  data-testid="copy-direct-apply-link-btn"
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Direct Apply Link
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#4B2D6E] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Briefcase className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-200">Total Jobs</p>
                <p className="text-3xl font-bold">{jobs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#4CAF50] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Eye className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-100">Active Listings</p>
                <p className="text-3xl font-bold">{activeJobs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#E8722A] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Briefcase className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-100">Total Applications</p>
                <p className="text-3xl font-bold">{totalApplications}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#4B2D6E] border-t-transparent"></div>
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-[#666666]">No job listings yet</p>
            <p className="text-sm text-[#666666] mt-1">Click &ldquo;Create Job&rdquo; to add your first listing</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.map((job) => (
            <Card 
              key={job.id} 
              className={`border-t-4 ${job.status === 'active' ? 'border-[#4CAF50]' : 'border-gray-300'}`}
              data-testid={`job-card-${job.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg text-[#4B2D6E]">{job.title}</CardTitle>
                    <Badge className={job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {job.status}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(job)} data-testid={`edit-job-${job.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => deleteJob(job.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-[#666666]">
                  <Clock className="h-4 w-4" />
                  <span>{job.position_type}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#666666]">
                  <MapPin className="h-4 w-4" />
                  <span>{job.service_area}</span>
                </div>
                <p className="text-sm text-[#333333] line-clamp-2">{job.description}</p>
                
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-sm text-[#666666]">
                    {job.applications || 0} applications
                  </span>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => toggleJobStatus(job)}
                    data-testid={`toggle-status-${job.id}`}
                  >
                    {job.status === 'active' ? (
                      <><EyeOff className="mr-1 h-3 w-3" /> Pause</>
                    ) : (
                      <><Eye className="mr-1 h-3 w-3" /> Activate</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyJobLink(job.id)}
                    className="text-[#E8722A] border-[#E8722A] hover:bg-[#E8722A]/5 flex-1"
                    data-testid={`copy-job-link-${job.id}`}
                  >
                    <Copy className="mr-1 h-3 w-3" /> Share Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobListingsPage;
